// Basketball workspace — shared presentational kit (ported from the CLAUDE DESIGN
// handoff: bb-primitives.jsx + bb-shell.jsx). Icons, visual primitives (Jersey,
// Seam, ConvergenceDots, LiveCounter, CalibrationTicker, PaceScale, MarginLine,
// CountdownClock, MiniShotChart, GlobalDots, CompoundStair, OtArc), page chrome
// (BbHead, AwaitPanel, Loading), section taxonomy, the shade() util, and the
// honest formatters. All styling via the scoped `.bb` layer in styles/basketball.css.

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { PERSONAS, PERSONA_ORDER, type Big6Persona } from "./big6Registry.ts";
import { useRpcQuery } from "../../data/useRpcQuery.ts";

// ── Section taxonomy ──────────────────────────────────────────────
export type BasketballSection =
  | "overview" | "slate" | "signals" | "clv" | "singles"
  | "first-half" | "night-shift" | "q1-strike" | "hardwood-slip"
  | "powerball" | "spread-fade" | "gauntlet" | "no-bets" | "leagues"
  | "big6-totals" | "big6-spread" | "big6-props" | "big6-global" | "big6-nonnba" | "big6-b2b";

export interface SectionMeta { crumb: string; kicker: string; title: string }
export const sectionMeta: Record<string, SectionMeta> = {
  overview: { crumb: "Dashboard", kicker: "Dashboard", title: "Basketball" },
  slate: { crumb: "Today's Slate", kicker: "Today's Slate", title: "Basketball" },
  signals: { crumb: "Signals", kicker: "Signals", title: "Basketball" },
  singles: { crumb: "BBALL SINGLES", kicker: "BBALL SINGLES", title: "Basketball" },
  "first-half": { crumb: "First Half Intelligence", kicker: "First Half Intelligence", title: "Basketball" },
  "night-shift": { crumb: "The Night Shift", kicker: "The Night Shift", title: "Basketball" },
  "q1-strike": { crumb: "Q1 Strike", kicker: "Q1 Strike", title: "Basketball" },
  "hardwood-slip": { crumb: "Hardwood Slip", kicker: "Hardwood Slip", title: "Basketball" },
  powerball: { crumb: "POWERBALL", kicker: "POWERBALL", title: "Basketball" },
  "spread-fade": { crumb: "Spread Fade", kicker: "Spread Fade", title: "Basketball" },
  gauntlet: { crumb: "The Hardwood Gauntlet", kicker: "The Hardwood Gauntlet", title: "Basketball" },
  "big6-totals": { crumb: "BIG 6 · The Totals Maven", kicker: "THE BIG 6 · TOTALS", title: "The Totals Maven" },
  "big6-spread": { crumb: "BIG 6 · The Spread Sharp", kicker: "THE BIG 6 · SPREAD", title: "The Spread Sharp" },
  "big6-props": { crumb: "BIG 6 · The Prop Modeller", kicker: "THE BIG 6 · PROPS", title: "The Prop Modeller" },
  "big6-global": { crumb: "BIG 6 · The Global Volume", kicker: "THE BIG 6 · GLOBAL", title: "The Global Volume" },
  "big6-nonnba": { crumb: "BIG 6 · The Overlooked Market", kicker: "THE BIG 6 · NON-NBA", title: "The Overlooked Market" },
  "big6-b2b": { crumb: "BIG 6 · Sharp Collective", kicker: "THE BIG 6 · CONVERGENCE", title: "Sharp Collective" },
  clv: { crumb: "CLV Track", kicker: "CLV Track", title: "Basketball" },
  "no-bets": { crumb: "No-Bet Report", kicker: "No-Bet Report", title: "Basketball" },
  leagues: { crumb: "Leagues", kicker: "Leagues", title: "Basketball" },
};

// ── Utils + formatters ────────────────────────────────────────────
export function shade(hex: string, percent: number): string {
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) + percent;
  let g = ((num >> 8) & 0xff) + percent;
  let b = (num & 0xff) + percent;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}

export const DASH = "—";
export function fmtOdds(o: number | string | null | undefined): string {
  if (typeof o === "string") return o.trim() || DASH;
  return typeof o === "number" && o > 0 ? o.toFixed(2) : DASH;
}
export function fmtPct(n: number | string | null | undefined): string {
  if (typeof n === "string") return n.trim() || DASH;
  return typeof n === "number" && Number.isFinite(n) ? `${n > 0 ? "+" : ""}${n.toFixed(1)}%` : DASH;
}
export function fmtProb(p: number | null | undefined): string {
  return typeof p === "number" && Number.isFinite(p) ? `${(p * 100).toFixed(1)}%` : DASH;
}
export function fmtLine(l: number | string | null | undefined, sign = false): string {
  if (l == null || l === "") return DASH;
  if (typeof l === "string") return l;
  return sign && l > 0 ? `+${l}` : String(l);
}
export function fmtTip(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return typeof iso === "string" ? iso : DASH;
  return d.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" }).replace(",", " ·");
}

// ── Icons ─────────────────────────────────────────────────────────
export function BIcon({ name, size = 16, color = "currentColor" }: { name: string; size?: number; color?: string }): JSX.Element {
  const c = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const p: Record<string, ReactNode> = {
    ball: (<><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3v18" /><path d="M5 6c3 3 3 9 0 12" /><path d="M19 6c-3 3-3 9 0 12" /></>),
    court: (<><rect x="3" y="5" width="18" height="14" rx="1" /><path d="M12 5v14" /><circle cx="12" cy="12" r="2.5" /><path d="M3 9h3v6H3M21 9h-3v6h3" /></>),
    star: <path d="m12 2 3 7 7 .5-5.5 4.5L18 21l-6-4-6 4 1.5-7L2 9.5 9 9z" />,
    chev: <path d="m9 6 6 6-6 6" />,
    chevDown: <path d="m6 9 6 6 6-6" />,
    arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
    bolt: <path d="m13 2-9 12h7l-1 8 9-12h-7l1-8Z" />,
    moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />,
    night: (<><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" /><circle cx="17" cy="6" r="0.8" fill={color} /><circle cx="14" cy="3" r="0.6" fill={color} /></>),
    clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
    target: (<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" fill={color} /></>),
    fade: (<><path d="M3 12c4-6 14-6 18 0" /><path d="M3 18c4-6 14-6 18 0" opacity="0.5" /><path d="M3 6c4-6 14-6 18 0" opacity="0.5" /></>),
    chain: (<><rect x="3" y="9" width="9" height="6" rx="3" /><rect x="12" y="9" width="9" height="6" rx="3" /></>),
    trophy: (<><path d="M6 4h12v4a6 6 0 0 1-12 0Z" /><path d="M6 6H3v2a3 3 0 0 0 3 3M18 6h3v2a3 3 0 0 1-3 3" /><path d="M10 14h4v3l2 3H8l2-3z" /></>),
    check: <path d="m5 12 4 4L19 6" />,
    minus: <path d="M5 12h14" />,
    flag: (<><path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" /></>),
    layers: (<><path d="m12 3 9 5-9 5-9-5z" /><path d="m3 13 9 5 9-5" /></>),
    refresh: (<><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></>),
    trend: (<><path d="M3 17l6-6 4 4 8-8" /><path d="M21 7h-5M21 7v5" /></>),
    plus: <path d="M12 5v14M5 12h14" />,
    search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
    map: (<><path d="M3 6v15l6-3 6 3 6-3V3l-6 3-6-3z" /><path d="M9 3v15M15 6v15" /></>),
    info: (<><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" /></>),
    decline: (<><circle cx="12" cy="12" r="9" /><path d="m8 8 8 8M16 8l-8 8" /></>),
    eye: (<><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></>),
    alert: (<><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></>),
  };
  return <svg {...c}>{p[name] ?? null}</svg>;
}

export function BBMark({ size = 18 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3 12h18M5.5 6c3 3 3 9 0 12M18.5 6c-3 3-3 9 0 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// ── Jersey — index chip ───────────────────────────────────────────
export function Jersey({ n, color = "var(--bb-orange)", size = 28, dim = false }: { n: ReactNode; color?: string; size?: number; dim?: boolean }): JSX.Element {
  return (
    <span className="bb-jersey" style={{ width: size, height: size, borderColor: dim ? "var(--border)" : color, color: dim ? "var(--text-tertiary)" : color }}>
      <span className="bb-jersey-n" style={{ fontSize: size * 0.55 }}>{n}</span>
      <span className="bb-jersey-stripes" aria-hidden="true" />
    </span>
  );
}

// ── Seam — basketball-seam horizontal rule ────────────────────────
export function Seam({ color = "var(--border)", opacity = 1 }: { color?: string; opacity?: number }): JSX.Element {
  return (
    <div className="bb-seam" style={{ opacity }}>
      <svg width="100%" height="14" viewBox="0 0 1000 14" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 7 L 480 7 C 488 7 491 1 500 1 C 509 1 512 13 521 13 C 530 13 533 7 540 7 L 1000 7" fill="none" stroke={color} strokeWidth="1" />
      </svg>
    </div>
  );
}

// ── Convergence dots — which of the 6 personas converged ──────────
export function ConvergenceDots({ converged = [], label, dense = false }: { converged?: readonly string[]; label?: string; dense?: boolean }): JSX.Element {
  return (
    <div className={`bb-conv ${dense ? "dense" : ""}`}>
      <div className="bb-conv-dots">
        {PERSONA_ORDER.map((slug) => {
          const on = converged.includes(slug);
          const color = PERSONAS[slug].accent;
          return (
            <span
              key={slug}
              className={`bb-conv-dot ${on ? "on" : ""}`}
              style={on ? { background: color, borderColor: color, boxShadow: `0 0 0 2px ${color}33` } : undefined}
              title={PERSONAS[slug].name + (on ? " · converged" : "")}
            />
          );
        })}
      </div>
      {label ? <span className="bb-conv-label">{label}</span> : null}
    </div>
  );
}

// ── Live counter — animates an integer from 0 to target ───────────
export function LiveCounter({ value, duration = 1400, format = (n: number) => n.toLocaleString() }: { value: number; duration?: number; format?: (n: number) => string }): JSX.Element {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(eased * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [value, duration]);
  return <span>{format(n)}</span>;
}

// NOTE: CalibrationTicker (a hardcoded "live" closing-line stream of fictional
// BOS/DEN/MAD events) and MiniShotChart (a fabricated shot chart — we don't
// capture box scores) were removed 2026-05-29: they presented mock data as live
// on the dashboard. The dashboard now reads only real RPCs. Do not reintroduce
// fabricated sample data into shared kit.

// ── Pace scale — Totals Maven signature ───────────────────────────
export function PaceScale({ leaguePaceMin = 96, leaguePaceMax = 110, ourPace, bookPace, side = "under" }: { leaguePaceMin?: number; leaguePaceMax?: number; ourPace: number; bookPace: number; side?: string }): JSX.Element {
  const range = leaguePaceMax - leaguePaceMin;
  const oursPct = ((ourPace - leaguePaceMin) / range) * 100;
  const bookPct = ((bookPace - leaguePaceMin) / range) * 100;
  return (
    <div className="bb-pace">
      <div className="bb-pace-head"><span>Pace scale · NBA</span><span style={{ marginLeft: "auto" }}>{leaguePaceMin} — {leaguePaceMax} poss/48</span></div>
      <div className="bb-pace-track">
        <div className="bb-pace-band" style={{ left: "30%", width: "40%" }} />
        <div className="bb-pace-marker book" style={{ left: `${bookPct}%` }}><span className="lbl">BOOK {bookPace}</span></div>
        <div className="bb-pace-marker ours" style={{ left: `${oursPct}%` }}><span className="lbl">OURS {ourPace}</span></div>
      </div>
      <div className="bb-pace-foot"><span>slow</span><span className="side">{side === "under" ? "UNDER signal" : "OVER signal"}</span><span>fast</span></div>
    </div>
  );
}

// ── Margin line — Spread Sharp signature ──────────────────────────
export function MarginLine({ bookSpread, fairSpread, gap }: { bookSpread: string; fairSpread: string; gap: string; side?: string }): JSX.Element {
  const toX = (v: number) => 50 + (v / 15) * 50;
  const bx = toX(parseFloat(bookSpread));
  const fx = toX(parseFloat(fairSpread));
  return (
    <div className="bb-margin">
      <div className="bb-margin-head"><span>Margin line</span><span style={{ marginLeft: "auto" }}>Gap {gap}</span></div>
      <div className="bb-margin-track">
        <div className="bb-margin-axis" />
        <div className="bb-margin-zero" />
        <div className="bb-margin-pin book" style={{ left: `${bx}%` }}><span className="lab">BOOK</span><span className="v">{bookSpread}</span></div>
        <div className="bb-margin-pin fair" style={{ left: `${fx}%` }}><span className="v">{fairSpread}</span><span className="lab">FAIR</span></div>
        <div className="bb-margin-gap" style={{ left: `${Math.min(bx, fx)}%`, width: `${Math.abs(fx - bx)}%` }} />
      </div>
      <div className="bb-margin-foot"><span>−15</span><span>0</span><span>+15</span></div>
    </div>
  );
}

// ── Countdown clock — Prop Modeller signature ─────────────────────
export function CountdownClock({ minutes = 22 }: { minutes?: number }): JSX.Element {
  const total = 60;
  const pct = Math.max(0, Math.min(1, minutes / total));
  const dash = 2 * Math.PI * 16;
  const dashOffset = dash * (1 - pct);
  const danger = minutes <= 10;
  const warn = minutes <= 25 && !danger;
  return (
    <div className={`bb-clock ${danger ? "danger" : warn ? "warn" : ""}`}>
      <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
        <circle cx="22" cy="22" r="16" fill="none" stroke="var(--bg-inset)" strokeWidth="3" />
        <circle cx="22" cy="22" r="16" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={dash} strokeDashoffset={dashOffset} strokeLinecap="round" transform="rotate(-90 22 22)" />
      </svg>
      <div className="t"><span className="v">{minutes}</span><span className="u">min</span></div>
    </div>
  );
}

// ── Global dots — Global Volume signature ─────────────────────────
export function GlobalDots(): JSX.Element {
  const dots = [
    { x: 22, y: 38, size: 8 }, { x: 24, y: 50, size: 5 }, { x: 23, y: 33, size: 4 },
    { x: 50, y: 36, size: 7 }, { x: 48, y: 42, size: 5 }, { x: 52, y: 38, size: 5 },
    { x: 49, y: 39, size: 5 }, { x: 80, y: 70, size: 6 }, { x: 84, y: 44, size: 5 },
  ];
  return (
    <div className="bb-globe">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <path d="M 12 30 Q 18 22 25 28 L 28 40 L 22 55 L 18 50 L 14 42 Z" fill="var(--bg-inset)" stroke="var(--border-faint)" strokeWidth="0.3" />
        <path d="M 22 56 Q 26 60 28 70 L 24 80 L 20 75 L 22 65 Z" fill="var(--bg-inset)" stroke="var(--border-faint)" strokeWidth="0.3" />
        <path d="M 42 28 Q 52 24 58 32 L 56 42 L 48 44 L 42 38 Z" fill="var(--bg-inset)" stroke="var(--border-faint)" strokeWidth="0.3" />
        <path d="M 48 44 Q 54 56 50 68 L 44 60 L 46 52 Z" fill="var(--bg-inset)" stroke="var(--border-faint)" strokeWidth="0.3" />
        <path d="M 56 32 Q 72 24 84 32 L 86 44 L 76 48 L 62 42 Z" fill="var(--bg-inset)" stroke="var(--border-faint)" strokeWidth="0.3" />
        <path d="M 76 68 Q 84 64 86 74 L 80 78 Z" fill="var(--bg-inset)" stroke="var(--border-faint)" strokeWidth="0.3" />
        {dots.map((d, i) => (
          <g key={i}>
            <circle cx={d.x} cy={d.y} r={d.size / 1.5} fill="var(--ppc, var(--bb-orange))" opacity="0.18" />
            <circle cx={d.x} cy={d.y} r="1.4" fill="var(--ppc, var(--bb-orange))" />
          </g>
        ))}
      </svg>
    </div>
  );
}

// ── Compound stair — Hardwood Slip / POWERBALL signature ──────────
export interface StairLeg { cum: string; odds: string }
export function CompoundStair({ legs }: { legs: readonly StairLeg[] }): JSX.Element {
  const max = Math.max(...legs.map((l) => parseFloat(l.cum)), 1);
  return (
    <div className="bb-stair">
      {legs.map((leg, i) => {
        const h = (parseFloat(leg.cum) / max) * 100;
        return (
          <div key={i} className="bb-stair-step">
            <div className="bb-stair-bar" style={{ height: `${h}%` }}><span className="bb-stair-v">{leg.cum}×</span></div>
            <div className="bb-stair-i">Leg {i + 1}</div>
            <div className="bb-stair-o">{leg.odds}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── OT trap arc — Overtime Trap signature ─────────────────────────
export function OtArc({ us, book, gap }: { us: number; book: number; gap: number; recommended?: string; odds?: string; conf?: string }): JSX.Element {
  const r = 70, c = 88, ARC_TOTAL = 270, START = 135, max = 25;
  const toAngle = (pct: number) => START + Math.min(pct / max, 1) * ARC_TOTAL;
  const usAng = toAngle(us), bookAng = toAngle(book);
  const polar = (cx: number, cy: number, rr: number, deg: number) => { const rad = (deg * Math.PI) / 180; return { x: cx + rr * Math.cos(rad), y: cy + rr * Math.sin(rad) }; };
  const arcPath = (cx: number, cy: number, rr: number, a1: number, a2: number) => {
    const p1 = polar(cx, cy, rr, a1), p2 = polar(cx, cy, rr, a2);
    const large = a2 - a1 > 180 ? 1 : 0;
    return `M ${p1.x} ${p1.y} A ${rr} ${rr} 0 ${large} 1 ${p2.x} ${p2.y}`;
  };
  return (
    <div className="bb-ot-arc">
      <svg viewBox="0 0 176 176" width="100%" aria-hidden="true">
        <path d={arcPath(c, c, r, START, START + ARC_TOTAL)} fill="none" stroke="var(--bg-inset)" strokeWidth="10" strokeLinecap="round" />
        <path d={arcPath(c, c, r, START, bookAng)} fill="none" stroke="var(--text-tertiary)" strokeWidth="10" strokeLinecap="round" opacity="0.7" />
        <path d={arcPath(c, c, r, bookAng, usAng)} fill="none" stroke="var(--bb-orange)" strokeWidth="10" strokeLinecap="round" />
        <g textAnchor="middle">
          <text x={c} y={c - 6} fill="var(--bb-orange-text)" fontFamily="var(--bb-display)" fontSize="48">+{gap.toFixed(1)}<tspan fontSize="22">%</tspan></text>
          <text x={c} y={c + 16} fontFamily="var(--font-mono)" fontSize="9" letterSpacing="1.8" fill="var(--text-tertiary)">GAP</text>
        </g>
      </svg>
      <div className="bb-ot-arc-legend">
        <div className="row"><span className="d book" /><span>Book implied</span><span className="v">{book.toFixed(1)}%</span></div>
        <div className="row"><span className="d us" /><span>STRIKZA</span><span className="v">{us.toFixed(1)}%</span></div>
      </div>
    </div>
  );
}

// ── Page chrome ───────────────────────────────────────────────────
// Status reflects the two-axis maturity model (sportsRegistry):
//   • registry-live  ⇒ "Pilot · live"
//   • + calibrating  ⇒ adds a second " · calibrating" chip (honest dual
//                       badge while CLV/Brier track-record accrues)
//   • not live       ⇒ legacy default text
// The default is computed live from sportsRegistry; per-page callers can
// still pass an explicit `status` to override.
import { isSportLive, isSportCalibrating } from "../../sports/sportsRegistry.ts";

function defaultBbStatus(): string {
  const live = isSportLive("basketball");
  const calibrating = isSportCalibrating("basketball");
  if (live && calibrating) return "Pilot · live · calibrating";
  if (live) return "Pilot · live";
  return "In calibration · not yet live";
}

export function BbHead({ kicker, title, status, accent }: { kicker: string; title: string; status?: string; accent?: string }): JSX.Element {
  const markStyle: CSSProperties | undefined = accent ? { background: `linear-gradient(140deg, ${accent} 0%, ${shade(accent, -20)} 100%)` } : undefined;
  const resolved = status ?? defaultBbStatus();
  return (
    <div className="bb-kicker-rail">
      <div className="bb-mark" style={markStyle}><BBMark size={20} /></div>
      <div className="bb-title-stack">
        <div className="bb-kicker"><span>Basketball</span><span className="sep">·</span><span className="acc">{kicker}</span></div>
        <div className="bb-title">{title}</div>
      </div>
      <div className="bb-status"><span className="pulse" /><span>{resolved}</span></div>
    </div>
  );
}

export function AwaitPanel({ kicker = "CALIBRATING", title, body, ghosts = 5, icon = "clock" }: { kicker?: string; title: string; body: string; ghosts?: number; icon?: string }): JSX.Element {
  return (
    <div className="bb-await">
      <div className="bb-await-mark"><BIcon name={icon} size={24} /></div>
      <div className="bb-await-body"><div className="k">{kicker}</div><h3>{title}</h3><p>{body}</p></div>
      <div className="bb-await-ghosts">{Array.from({ length: ghosts }).map((_, i) => <div key={i} className="g" />)}</div>
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }): JSX.Element {
  return <div className="bb-loading"><span className="bb-loading-dot" />{label}</div>;
}

/** Hook to read a no-arg jsonb RPC. Honest: empty/null is the published state.
 *  Backed by the shared data layer (cache + return/focus refresh). */
export function useRpc<T>(fn: string): { loading: boolean; error: string | null; data: T | null } {
  const q = useRpcQuery<T | null>(fn);
  return { loading: q.isLoading, error: q.error ? q.error.message : null, data: (q.data ?? null) as T | null };
}
